import { useEffect, useState } from "react";
import { Badge, Button, ErrorText, Field, inputCls, Json, Panel } from "../../components/ui";
import { ChannelHeader } from "./Channels";
import { get, post, put } from "../../lib/api";

type Settings = {
  enabled: boolean;
  unknownUsers: "refuse" | "create";
  hasBotToken: boolean;
  hasSigningSecret: boolean;
  team: string | null;
  botUserId: string | null;
  botName: string | null;
  eventsUrl: string;
};

const SCOPES = [
  "app_mentions:read", "chat:write", "im:history", "im:read", "im:write", "channels:history", "groups:history",
  "mpim:history", "users:read", "users:read.email", "files:read", "files:write", "reactions:write",
];
const EVENTS = ["app_mention", "message.im", "message.channels", "message.groups", "message.mpim"];

function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2">
      <code className="min-w-0 flex-1 overflow-x-auto rounded-md bg-fill px-2 py-1.5 font-mono text-xs whitespace-nowrap">{value}</code>
      <Button
        size="sm"
        className="shrink-0"
        onClick={async () => {
          await navigator.clipboard.writeText(value).catch(() => {});
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </div>
  );
}

export function AdminSlack() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [botToken, setBotToken] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState<unknown>(null);

  useEffect(() => {
    get<Settings>("/api/slack/settings").then(setSettings, (e) => setError(e.message));
  }, []);

  const save = async (body: Record<string, unknown>, message: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      setSettings(await put<Settings>("/api/slack/settings", body));
      setNotice(message);
      setBotToken("");
      setSigningSecret("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return <p className="p-6 text-sm text-muted">{error ?? "Loading…"}</p>;
  const configured = settings.hasBotToken && settings.hasSigningSecret;

  return (
    <div>
      <ChannelHeader
        brand="slack"
        name="Slack"
        subtitle="Pandas Bot in Slack: DM it or @mention it in a channel. It replies in threads, delegates live and shares files."
        actions={settings.team && <Badge tone="blue">{settings.team}</Badge>}
        on={settings.enabled}
        toggleDisabled={busy || !configured}
        toggleTitle={configured ? undefined : "Save the credentials first"}
        onToggle={(enabled) => void save({ enabled }, enabled ? "Slack bot enabled." : "Slack bot disabled.")}
        offText={configured ? "Off: Pandas Bot doesn't answer in Slack." : "Off: save the credentials below, then switch Slack on."}
      />
      <div className="grid grid-cols-1 gap-6 px-4 pb-5 md:px-6 xl:grid-cols-2">
        <Panel title="Connection" className="self-start">
          <div className="space-y-4">
            <Field label="Bot User OAuth Token" hint={settings.hasBotToken ? `Stored${settings.botName ? ` · bot @${settings.botName}` : ""}. Paste a new one to replace it.` : "OAuth & Permissions → Bot User OAuth Token (xoxb-…)"}>
              <input type="password" autoComplete="off" className={`${inputCls} font-mono`} placeholder={settings.hasBotToken ? "•••••• stored" : "xoxb-…"} value={botToken} onChange={(e) => setBotToken(e.target.value)} />
            </Field>
            <Field label="Signing Secret" hint={settings.hasSigningSecret ? "Stored. Paste a new one to replace it." : "Basic Information → App Credentials → Signing Secret"}>
              <input type="password" autoComplete="off" className={`${inputCls} font-mono`} placeholder={settings.hasSigningSecret ? "•••••• stored" : "signing secret"} value={signingSecret} onChange={(e) => setSigningSecret(e.target.value)} />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" disabled={busy || (!botToken.trim() && !signingSecret.trim())} onClick={() => save({ botToken, signingSecret }, "Credentials saved and verified with Slack.")}>
                Save credentials
              </Button>
              <Button disabled={busy || !settings.hasBotToken} onClick={async () => setTest(await post("/api/slack/test"))}>
                Test connection
              </Button>
            </div>
            {test !== null && <Json value={test} />}

            <div className="space-y-3 border-t border-line pt-4">
              <Field label="People without an AgentOS account" hint="Slack users are matched to Pandas users by email.">
                <select className={inputCls} value={settings.unknownUsers} disabled={busy} onChange={(e) => save({ unknownUsers: e.target.value }, "Saved.")}>
                  <option value="refuse">Refuse politely — an admin adds them first</option>
                  <option value="create">Create them automatically as members</option>
                </select>
              </Field>
            </div>
            {notice && <p className="rounded bg-success/10 px-3 py-2 text-sm text-success">{notice}</p>}
            <ErrorText error={error} />
          </div>
        </Panel>

        <Panel title="Slack app setup" className="self-start">
          <div className="space-y-4 text-sm">
            <ol className="list-decimal space-y-3 pl-5">
              <li>
                <b>OAuth &amp; Permissions → Bot Token Scopes</b>: add
                <div className="mt-1.5 flex flex-wrap gap-1">{SCOPES.map((s) => <Badge key={s}>{s}</Badge>)}</div>
              </li>
              <li><b>App Home</b>: enable the Messages Tab and allow users to send messages.</li>
              <li><b>Install to workspace</b>, then paste the Bot User OAuth Token and Signing Secret here and save.</li>
              <li>
                <b>Event Subscriptions</b>: enable and set the Request URL (Slack verifies it immediately — save credentials here first):
                <div className="mt-1.5"><CopyValue value={settings.eventsUrl} /></div>
              </li>
              <li>
                Under <b>Subscribe to bot events</b> add
                <div className="mt-1.5 flex flex-wrap gap-1">{EVENTS.map((s) => <Badge key={s}>{s}</Badge>)}</div>
                then <b>Save Changes</b> and reinstall if Slack asks.
              </li>
              <li>Switch Slack <b>on</b> at the top of this page, then DM the bot or @mention it in a channel (invite it with <code>/invite @{settings.botName ?? "bot"}</code>).</li>
            </ol>
            <p className="text-xs text-muted">In DMs, send <code>new</code> to start a fresh conversation. DM conversations also reset after 8 hours of inactivity.</p>
          </div>
        </Panel>
      </div>
    </div>
  );
}
